import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/cn";
import { SpotlightCard } from "../ui";
import {
  Search,
  BarChart3,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Wrench,
  TrendingUp,
  FlaskConical,
  Zap,
} from "lucide-react";

interface PlatformOverviewProps {
  defaultExpanded?: boolean;
}

// Compact dimension colors
const dimColors = {
  bip: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  tf: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  cp: {
    bg: "bg-purple-50",
    text: "text-purple-700",
    border: "border-purple-200",
  },
  rr: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
};

// Feature icon styles
const featureIconStyles = {
  mining: "bg-violet-500",
  scoring: "bg-blue-500",
  insights: "bg-amber-500",
};

export function PlatformOverview({
  defaultExpanded = false,
}: PlatformOverviewProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(
    defaultExpanded ? undefined : 0,
  );

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      if (isExpanded) {
        setHeight(contentRef.current.scrollHeight);
      } else {
        setHeight(0);
      }
    }
  }, [isExpanded]);

  // Compact features with icons only
  const features = [
    {
      key: "mining" as const,
      icon: Search,
      label: t("advantages.mining.title"),
    },
    {
      key: "scoring" as const,
      icon: BarChart3,
      label: t("advantages.scoring.title"),
    },
    {
      key: "insights" as const,
      icon: Lightbulb,
      label: t("advantages.insights.title"),
    },
  ];

  // V3 Dimensions for compact display (GB removed)
  const v3Dimensions = [
    {
      key: "bip" as const,
      icon: DollarSign,
      title: t("scoring.dimensions.bip.title"),
      weight: "37%",
    },
    {
      key: "tf" as const,
      icon: Wrench,
      title: t("scoring.dimensions.tf.title"),
      weight: "26%",
    },
    {
      key: "cp" as const,
      icon: TrendingUp,
      title: t("scoring.dimensions.cp.title"),
      weight: "21%",
    },
    {
      key: "rr" as const,
      icon: FlaskConical,
      title: t("scoring.dimensions.rr.title"),
      weight: "16%",
    },
  ];

  const grades = [
    {
      key: "high",
      range: t("scoring.grades.high.range"),
      label: t("scoring.grades.high.label"),
      cls: "bg-emerald-50 text-emerald-700",
    },
    {
      key: "medium",
      range: t("scoring.grades.medium.range"),
      label: t("scoring.grades.medium.label"),
      cls: "bg-blue-50 text-blue-700",
    },
    {
      key: "low",
      range: t("scoring.grades.low.range"),
      label: t("scoring.grades.low.label"),
      cls: "bg-amber-50 text-amber-700",
    },
    {
      key: "minimal",
      range: t("scoring.grades.minimal.range"),
      label: t("scoring.grades.minimal.label"),
      cls: "bg-slate-100 text-slate-600",
    },
  ];

  return (
    <SpotlightCard
      className="overflow-hidden"
      spotlightColor="rgba(30, 64, 175, 0.06)"
    >
      <div className="p-3 md:p-4">
        {/* Unified Header Row: Features + CAV Toggle */}
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl cursor-pointer",
            "bg-gradient-to-r from-[#1E40AF]/5 via-[#3B82F6]/5 to-[#1E40AF]/5",
            "border border-[#1E40AF]/10 hover:border-[#1E40AF]/20",
            "transition-colors duration-200",
          )}
          onClick={toggleExpand}
        >
          {/* Left: Features + CAV Title */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Compact Feature Icons */}
            <div className="flex items-center gap-1.5">
              {features.map((feature) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={feature.key}
                    className={cn(
                      "w-6 h-6 rounded-md flex items-center justify-center",
                      featureIconStyles[feature.key],
                    )}
                    title={feature.label}
                  >
                    <IconComponent className="w-3.5 h-3.5 text-white" />
                  </div>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px h-5 bg-slate-300 dark:bg-slate-600" />

            {/* CAV Title */}
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] flex items-center justify-center">
                <BarChart3 className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <div className="text-xs font-bold text-[#1E40AF] dark:text-[#3B82F6] flex items-center gap-1.5">
                  {t("scoring.formula")}
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium flex items-center gap-0.5">
                    <Zap className="w-2.5 h-2.5" />
                    V3
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 hidden sm:block">
                  {t("scoring.intro")}
                </div>
              </div>
            </div>

            {/* Right: Dimensions Preview + Expand Toggle */}
            <div className="flex items-center gap-2">
              {/* Compact Dimension Pills (visible when collapsed) */}
              {!isExpanded && (
                <div className="hidden md:flex items-center gap-1">
                  {v3Dimensions.slice(0, 4).map((dim) => {
                    const colors = dimColors[dim.key];
                    return (
                      <span
                        key={dim.key}
                        className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded font-medium",
                          colors.bg,
                          colors.text,
                        )}
                      >
                        {dim.weight}
                      </span>
                    );
                  })}
                </div>
              )}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </div>
          </div>
        </div>

        {/* Collapsible Scoring Details */}
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ height: height !== undefined ? `${height}px` : "auto" }}
        >
          <div ref={contentRef} className="pt-3">
            {/* Formula Display - Inline */}
            <div className="mb-3 px-2 py-1.5 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs font-medium text-slate-700">
                {t("scoring.v3formula")}
              </div>
              <div className="text-[10px] text-slate-500">
                {t("scoring.bonusNote")}
              </div>
            </div>

            {/* V3 Dimensions - Compact Horizontal */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {v3Dimensions.map((dim) => {
                const colors = dimColors[dim.key];
                const IconComponent = dim.icon;
                return (
                  <div
                    key={dim.key}
                    className={cn(
                      "flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs",
                      colors.bg,
                      colors.text,
                      colors.border,
                    )}
                  >
                    <IconComponent className="w-3 h-3" />
                    <span className="font-medium">{dim.title}</span>
                    <span className="font-bold">{dim.weight}</span>
                  </div>
                );
              })}
            </div>

            {/* Grade Levels - Compact inline */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] text-slate-500">
                {t("scoring.gradesLabel")}
              </span>
              {grades.map((grade) => (
                <span
                  key={grade.key}
                  className={cn(
                    "text-[10px] px-1.5 py-0.5 rounded font-medium",
                    grade.cls,
                  )}
                >
                  {grade.range} {grade.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SpotlightCard>
  );
}
